import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Alert, Button, Heading, Sheet, Text, Tooltip } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { useStore } from "zustand";

import { ComplianceState } from "../../../../api/adsApi/adsApi.interfaces";
import { getDiscoveryApiClient } from "../../../../api/discoveryApi/discoveryApi";
import { ACCOUNT_AGNOSTIC_VIEW } from "../../../../config/constants";
import { COPY_MESSAGES, TOOLTIP_MESSAGES } from "../../../../constants/common";
import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../../../constants/telemetry";
import { useCreateInsightLinkageAndDownload } from "../../../../hooks/insightLinkage/useCreateInsightLinkageAndDownload";
import { useAsyncErrorStatusNotification } from "../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { editContentStore } from "../../../../store/editContent";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import type { Compliance } from "../../../../store/insightDetails";
import {
    defaultInfo,
    insightDetailsStore,
} from "../../../../store/insightDetails";
import { InsightLinkageStore } from "../../../../store/insightLinkage";
import { shareContentStore } from "../../../../store/shareContent";
import { workbenchStore } from "../../../../store/workbench";
import { ACCOUNT_AGNOSTIC_IDS } from "../../../../types/common";
import {
    generateSharableLinkIdSync,
    generateShareableUrl,
    isLoading,
} from "../../../../utils/api.utils";
import { copyToClipboard } from "../../../../utils/clipboard.utils";
import { getMarketingContentAndPitchUrl } from "../../../../utils/downloadUrl.utils";
import {
    getAdvertiserId,
    QueryParam,
} from "../../../../utils/permission.utils";
import { getPhoenixContentInfoFromStore } from "../../../../utils/phoenix.utils";
import {
    logWithTakt,
    getFilterTelemetryDetails,
    getUnifiedFilterTelemetryDetails,
} from "../../../../utils/telemetry.utils";
import { AISummarySection } from "../../../common/AISummarySection";
import { InsightViewer } from "../../../common/InsightViewer";
import { ShareIcon } from "../../../common/ShareIcon";
import {
    HeaderCardDetails,
    FooterDownloadButton,
    FooterComponentBuilder,
} from "../../../common/insightDetail.components";
import { DivFlexSpaceBetween } from "../../../common/styled.components";
import { SharePitchedContentButton } from "../pitchedContents/SharePitchedContentButton";

import { InsightDetailSheetSkeleton } from "./InsightDetailSheetSkeleton";
import { WorkbenchActions } from "./WorkbenchActions";
import {
    PdfContainerSkeletonGenerationLoader,
    PdfContainerSkeletonLoader,
} from "./layout";
import {
    StyledDetailSheetDetailsContainer,
    StyledDetailSheetPDFViewerContainer,
    StyledInsightSheetBody,
    StyledInsightSheetHeader,
    StyledSheetFooter,
} from "./styles";

const formatAiSummary = (summary: string): string =>
    summary.replace(/\s*•\s*/g, "\n• ") +
    "\n\nDisclaimer: This summary is AI generated. Please review before use.";

export const InsightSelectionSheet: React.FunctionComponent = () => {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);

    const [numPages, setNumPages] = React.useState<number>();

    const [status, open, info, reset] = useStore(
        insightDetailsStore,
        (state) => [
            state.asyncStatus,
            state.open,
            state.info,
            state.resetState,
        ],
    );

    const [closeEditModal] = useStore(editContentStore, (state) => [
        state.closeEditModal,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [setOpen, setInsightLinkageMetadata] = useStore(
        InsightLinkageStore,
        (state) => [state.setOpen, state.setInsightLinkageMetadata],
    );

    const [resetExternalShare] = useStore(shareContentStore, (state) => [
        state.reset,
    ]);

    const {
        title,
        details,
        previewFileFormat,
        identifier,
        insightType,
        shareInsightType = insightType,
        previewAsset,
        downloadAsset,
        description,
        compliance,
        previewLoading,
        previewGenerating,
        downloadLoading,
        outputFileFormats = [],
        feedback,
        handleEditInsight,
        handleShareInsight,
        isShareEnabled,
        additionalDetails,
        template,
        content,
    } = info;

    const [selectedSlides, setSelectedSlides] = React.useState(
        new Set<number>(),
    );

    const isCompliant = compliance === undefined || compliance.isCompliant;

    useEffect(() => {
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();

        if (open && !isEmpty(identifier) && !isEmpty(insightType)) {
            logWithTakt(TELEMETRY_ID.INSIGHT_DETAILS, {
                ...filters,
                insightType,
                identifier,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.INSIGHT_DETAILS,
                PHOENIX_FEATURE.DISCOVERY,
                {
                    contentInfo: getPhoenixContentInfoFromStore(
                        insightType,
                        identifier,
                    ),
                    customData: {
                        filters,
                    },
                },
            );

            // Log AI content telemetry if AI title or summary is present
            if (content?.aiTitle || content?.aiSummary) {
                logWithTakt(TELEMETRY_ID.AI_SUMMARY_DROPDOWN_CLICK, {
                    action: "show",
                    contentId: content?.contentId,
                    contentType: content?.contentType,
                });
                PhoenixMetrics.emit(
                    TELEMETRY_ID.AI_SUMMARY_DROPDOWN_CLICK,
                    PHOENIX_FEATURE.AI,
                    {
                        contentInfo: getPhoenixContentInfoFromStore(
                            insightType,
                            identifier,
                        ),
                    },
                );
            }

            setSelectedSlides(getSlideIndexes(identifier));
        }
    }, [open, identifier, insightType]);

    const handleOnClose = () => {
        setNumPages(0);
        setSelectedSlides(new Set<number>());
        reset();
        closeEditModal();
        resetExternalShare();
    };

    const [
        marketingNarrativeDownloadUrlAsyncState,
        marketingNarrativeDownloadUrlAsyncActions,
    ] = useAsync(async (contentId: string, version: string) => {
        const data = await (
            await getDiscoveryApiClient()
        ).getContentDownloadUrl(contentId, version);
        return data.downloadUrl ?? "";
    });
    useAsyncErrorStatusNotification(marketingNarrativeDownloadUrlAsyncState);

    const [insightLinkageState, insightLinkageAction] =
        useCreateInsightLinkageAndDownload();
    useAsyncErrorStatusNotification(insightLinkageState);

    const handleDownload = async () => {
        const advertiserId = getAdvertiserId();
        const isMarketingNarrative = insightType === InsightType.MARKETING;
        const isMarketingContentWithoutAdvertiser =
            !advertiserId && isMarketingNarrative;
        const isPitchedContent = insightType === InsightType.PITCHED_CONTENT;

        let downloadUrl = downloadAsset?.downloadUrl ?? "";
        if (isPitchedContent) {
            downloadUrl = await getMarketingContentAndPitchUrl(
                insightType!,
                downloadAsset?.contentId,
                downloadAsset?.contentVersion,
                downloadAsset?.downloadUrl,
            );
        }

        // Pitched Content is already associated to sf opportunity
        if (isPitchedContent) {
            const filters = useUnifiedSearch
                ? getUnifiedFilterTelemetryDetails()
                : getFilterTelemetryDetails();

            logWithTakt(TELEMETRY_ID.INSIGHT_DOWNLOAD, {
                ...filters,
                insightType,
                identifier,
            });
            PhoenixMetrics.emit(
                TELEMETRY_ID.INSIGHT_DOWNLOAD,
                PHOENIX_FEATURE.DISCOVERY,
                {
                    contentInfo: getPhoenixContentInfoFromStore(
                        insightType,
                        identifier,
                    ),
                    customData: {
                        filters,
                    },
                },
            );
            window.open(downloadUrl, "_blank");
        } else if (isMarketingContentWithoutAdvertiser) {
            // If user is in account agnostic view, start download. Otherwise open modal
            await insightLinkageAction.execute({
                title,
                content: downloadAsset?.content,
                identifier,
                downloadUrl,
                insightType: insightType!,
                description: description ?? "",
                contentId: downloadAsset?.contentId,
                contentVersion: downloadAsset?.contentVersion,
            });
        } else {
            setInsightLinkageMetadata({
                title,
                content: downloadAsset?.content,
                identifier,
                downloadUrl,
                insightType: insightType!,
                description: description ?? "",
                contentId: downloadAsset?.contentId,
                contentVersion: downloadAsset?.contentVersion,
            });

            setOpen(true);
        }
        void reset();
    };

    const warning = useMemo(() => {
        const list: string[] = [];
        if (outputFileFormats.includes("zip")) {
            list.push(
                "Issue occured during generation process, download will be available as a zip package",
            );
        }
        return list;
    }, [outputFileFormats]);

    const getShareUrl = useCallback(() => {
        const baseUrl = window.location.origin + window.location.pathname;
        const identifier = info.identifier;

        if (!identifier) {
            return;
        }

        let shareUrl: string;

        switch (insightType) {
            case InsightType.WB: {
                const currentParams = new URLSearchParams(
                    window.location.search,
                );
                currentParams.set(QueryParam.IDENTIFIER, identifier);
                const urlParams = Object.fromEntries(currentParams.entries());
                shareUrl = generateShareableUrl(baseUrl, urlParams);
                break;
            }
            case InsightType.MARKETING: {
                const sharableIdentifier = generateSharableLinkIdSync(
                    identifier,
                    insightType,
                );
                const urlParams: Record<string, string> = {
                    [QueryParam.IDENTIFIER]: sharableIdentifier,
                    sfId: ACCOUNT_AGNOSTIC_IDS.WITH_IDENTIFIER,
                };
                shareUrl = generateShareableUrl(baseUrl, urlParams);
                break;
            }
            default: {
                const sharableIdentifier = generateSharableLinkIdSync(
                    identifier,
                    shareInsightType,
                );
                const urlParams: Record<string, string> = {
                    [QueryParam.IDENTIFIER]: sharableIdentifier,
                };
                const searchParams = new URLSearchParams(
                    window.location.search,
                );
                const currentFilter = searchParams.get("filters");
                const sfId = searchParams.get("sfId");

                if (sfId) {
                    urlParams.sfId = sfId;
                }

                if (currentFilter) {
                    urlParams.filters = currentFilter;
                }

                shareUrl = generateShareableUrl(baseUrl, urlParams);
                break;
            }
        }

        return shareUrl;
    }, [info.identifier, insightType]);

    const handleShareClick = async () => {
        const shareUrl = getShareUrl();
        if (!shareUrl) return;

        const copyMessage =
            insightType === InsightType.MARKETING
                ? COPY_MESSAGES.MARKETING
                : COPY_MESSAGES.INSIGHT;
        copyToClipboard(shareUrl, "share-url", copyMessage);
        const filters = useUnifiedSearch
            ? getUnifiedFilterTelemetryDetails()
            : getFilterTelemetryDetails();

        logWithTakt(TELEMETRY_ID.INSIGHT_SHARE_SUCCESS, {
            ...filters,
            identifier: info.identifier,
            shareType: "single",
        });
        PhoenixMetrics.emit(
            TELEMETRY_ID.INSIGHT_SHARE_SUCCESS,
            PHOENIX_FEATURE.DISCOVERY,
            {
                contentInfo: getPhoenixContentInfoFromStore(
                    info.insightType,
                    info.identifier,
                ),
                customData: {
                    filters,
                    shareType: "single",
                },
            },
        );
    };

    return (
        <div data-testid="insight-selection-sheet-container">
            <Sheet
                sheetElementId="ihub-portal"
                padding={"none"}
                data-testid={`${insightType}-insight-selection-sheet`}
                isOpen={open}
                onClose={() => void handleOnClose()}
                side="end"
                closeButton={{
                    type: "icon",
                    label: "",
                }}
            >
                {status && isLoading(status) ? (
                    <InsightDetailSheetSkeleton />
                ) : (
                    <StyledInsightSheetBody>
                        <StyledInsightSheetHeader>
                            <DivFlexSpaceBetween>
                                <Heading renderAs="h5">
                                    {title || "Insight"} &nbsp;&nbsp;
                                    <Text color={"tertiary"} inline={true}>
                                        {numPages !== undefined && numPages > 0
                                            ? `${numPages} slides`
                                            : `1 slide`}
                                    </Text>
                                    <ShareIcon
                                        onClick={() => void handleShareClick()}
                                        tooltipMessage={
                                            insightType ===
                                            InsightType.MARKETING
                                                ? TOOLTIP_MESSAGES.MARKETING
                                                : TOOLTIP_MESSAGES.INSIGHT
                                        }
                                        disabled={false}
                                        testId="insight-share-icon"
                                    />
                                </Heading>
                            </DivFlexSpaceBetween>
                            <AISummarySection
                                aiSummaryOverview={content?.aiTitle ?? ""}
                                aiSummaryExecutive={content?.aiSummary}
                                contentId={content?.contentId}
                                contentType={content?.contentType}
                                logExpandClick
                                dataTestId={`${identifier}-insight-selection-sheet-title-text`}
                            />
                            <HeaderCardDetails
                                details={details}
                                templateInfo={template}
                                content={content}
                                selectedSlides={selectedSlides}
                                setSelectedSlides={setSelectedSlides}
                                numPages={numPages}
                                previewAsset={previewAsset}
                                identifier={identifier}
                                insightType={insightType}
                                previewFileFormat={previewFileFormat}
                                downloadAsset={downloadAsset}
                                isCompliant={isCompliant}
                                outputFileFormats={outputFileFormats}
                            />
                            {warning && warning.length > 0 && (
                                <StyledDetailSheetDetailsContainer>
                                    <Alert variant="warning">
                                        {warning.map((t, i) => (
                                            <Text key={i}>{t}</Text>
                                        ))}
                                    </Alert>
                                </StyledDetailSheetDetailsContainer>
                            )}
                            {additionalDetails && additionalDetails}
                        </StyledInsightSheetHeader>
                        <StyledDetailSheetPDFViewerContainer>
                            {previewGenerating ? (
                                <PdfContainerSkeletonGenerationLoader />
                            ) : previewLoading ? (
                                <PdfContainerSkeletonLoader />
                            ) : (
                                previewAsset === defaultInfo.previewAsset || (
                                    <InsightViewer
                                        previewFileFormat={previewFileFormat}
                                        numPages={numPages!}
                                        setNumPages={setNumPages}
                                        previewAsset={previewAsset}
                                        insightType={insightType!}
                                        identifier={identifier}
                                        selectedSlides={selectedSlides}
                                        setSelectedSlides={setSelectedSlides}
                                        isCompliant={isCompliant}
                                    />
                                )
                            )}
                        </StyledDetailSheetPDFViewerContainer>
                        <StyledSheetFooter>
                            {(() => {
                                const footerComponents: JSX.Element[] = [];

                                if (
                                    insightType ===
                                        InsightType.PITCHED_CONTENT &&
                                    handleShareInsight
                                ) {
                                    footerComponents.push(
                                        <SharePitchedContentButton
                                            handleClick={handleShareInsight}
                                            disabled={!isShareEnabled}
                                        />,
                                    );
                                }

                                if (handleEditInsight) {
                                    footerComponents.push(
                                        <Button onClick={handleEditInsight}>
                                            Edit
                                        </Button>,
                                    );
                                }

                                footerComponents.push(
                                    <FooterDownloadButton
                                        handleDownload={() =>
                                            void handleDownload()
                                        }
                                        downloadAsset={downloadAsset}
                                        identifier={identifier}
                                        insightType={insightType}
                                        loading={downloadLoading}
                                        title={title}
                                    />,
                                    <WithComplianceToolTip
                                        compliance={compliance}
                                        element={
                                            <WorkbenchActions
                                                assetUrl={getShareUrl()}
                                                title={title}
                                                identifier={identifier}
                                                selectedSlides={selectedSlides}
                                                setSelectedSlides={
                                                    setSelectedSlides
                                                }
                                                outputFileFormats={
                                                    outputFileFormats
                                                }
                                                numPages={numPages!}
                                                downloadAsset={downloadAsset}
                                                previewAsset={previewAsset}
                                                insightType={insightType!}
                                                isCompliant={isCompliant}
                                            />
                                        }
                                    />,
                                );

                                return (
                                    <FooterComponentBuilder
                                        loading={false}
                                        handleClose={handleOnClose}
                                        identifier={identifier}
                                        insightType={insightType}
                                        footerComponents={footerComponents}
                                    />
                                );
                            })()}
                        </StyledSheetFooter>
                    </StyledInsightSheetBody>
                )}
            </Sheet>
        </div>
    );
};

interface ComplianceToolTipProps {
    compliance: Compliance | undefined;
    element: React.ReactElement;
}

const WithComplianceToolTip: React.FC<ComplianceToolTipProps> = ({
    compliance,
    element,
}) => {
    if (compliance?.isCompliant !== false) {
        return element;
    }

    const message =
        compliance?.status === ComplianceState.UNSUPPORTED_VIEW
            ? ACCOUNT_AGNOSTIC_VIEW
            : "This content isn't compatible with Workbench. Please download it";

    return (
        <Tooltip
            data-testid="compliance-tool-tip"
            trigger={<div>{element}</div>}
            message={message}
        />
    );
};
